import { Bell, Bot, CircleUserRound, LogOut, MoonStar, Search, SunMedium } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';

import { Button } from '@/components/primitives/button';
import { Input } from '@/components/primitives/input';
import { Select } from '@/components/primitives/select';
import { signOut } from '@/features/auth/services/auth-api';
import { useOnlineStatus } from '@/hooks/use-online-status';
import { initialsFromName } from '@/lib/utils';
import { useAssistantStore } from '@/state/assistant-store';
import { useSessionStore } from '@/state/session-store';
import { useUiStore } from '@/state/ui-store';

export function TopBar() {
  const navigate = useNavigate();
  const isOnline = useOnlineStatus();
  const { runtimeInfo, setAssistantOpen } = useAssistantStore(
    useShallow((state) => ({
      runtimeInfo: state.runtimeInfo,
      setAssistantOpen: state.setAssistantOpen,
    })),
  );
  const { memberships, activeOrganizationId, setActiveOrganizationId, profile } = useSessionStore(
    useShallow((state) => ({
      memberships: state.memberships,
      activeOrganizationId: state.activeOrganizationId,
      setActiveOrganizationId: state.setActiveOrganizationId,
      profile: state.profile,
    })),
  );
  const activeOrganization = memberships.find((membership) => membership.organization_id === activeOrganizationId)?.organization ?? null;
  const [search, setSearch] = useState('');
  const { theme, toggleTheme, setCommandPaletteOpen } = useUiStore(
    useShallow((state) => ({
      theme: state.theme,
      toggleTheme: state.toggleTheme,
      setCommandPaletteOpen: state.setCommandPaletteOpen,
    })),
  );

  return (
    <header className="sticky top-0 z-20 border-b border-white/10 bg-ink-950/70 px-4 py-4 backdrop-blur-xl sm:px-5 xl:px-6">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
        <div className="flex flex-1 flex-wrap items-center gap-3">
          <form
            className="relative min-w-[280px] flex-1"
            onSubmit={(event) => {
              event.preventDefault();
              if (search.trim()) {
                navigate(`/search?q=${encodeURIComponent(search.trim())}`);
              }
            }}
          >
            <Search className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-slate-500" />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search companies, documents, notes, tasks..."
              className="pl-10"
            />
          </form>

          <Select value={activeOrganizationId ?? ''} className="min-w-[220px]" onChange={(event) => setActiveOrganizationId(event.target.value)}>
            {memberships.map((membership) => (
              <option key={membership.organization_id} value={membership.organization_id}>
                {membership.organization?.name ?? 'Unknown organization'}
              </option>
            ))}
          </Select>

          {activeOrganization ? (
            <div className="hidden rounded-2xl border border-white/10 bg-white/5 px-4 py-2 xl:block">
              <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Active organization</p>
              <p className="mt-1 text-sm font-semibold text-white">{activeOrganization.name}</p>
            </div>
          ) : null}

          <Button variant="secondary" onClick={() => setCommandPaletteOpen(true)}>
            Quick actions
          </Button>
        </div>

        <div className="flex items-center gap-3">
          <div
            className={`rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] ${
              isOnline ? 'bg-emerald-500/15 text-emerald-300' : 'bg-amber-500/15 text-amber-300'
            }`}
          >
            {isOnline ? 'Online' : 'Offline cache'}
          </div>

          <Button variant="secondary" onClick={() => setAssistantOpen(true)} disabled={!runtimeInfo?.enabled}>
            <Bot className="h-4 w-4" />
            Assistant
          </Button>

          <button
            type="button"
            className="rounded-full border border-white/10 bg-white/5 p-2 text-slate-300 hover:bg-white/10 hover:text-white"
          >
            <Bell className="h-4 w-4" />
          </button>

          <button
            type="button"
            className="rounded-full border border-white/10 bg-white/5 p-2 text-slate-300 hover:bg-white/10 hover:text-white"
            onClick={toggleTheme}
            aria-label="Toggle color theme"
          >
            {theme === 'dark' ? <SunMedium className="h-4 w-4" /> : <MoonStar className="h-4 w-4" />}
          </button>

          <div className="flex items-center gap-3 rounded-full border border-white/10 bg-white/5 px-3 py-2">
            <button
              type="button"
              className="flex h-9 w-9 items-center justify-center rounded-full bg-accent-500/10 text-sm font-semibold text-accent-200"
              onClick={() => navigate('/profile')}
              aria-label="Open profile"
            >
              {profile?.full_name ? initialsFromName(profile.full_name) : <CircleUserRound className="h-4 w-4" />}
            </button>
            <div className="hidden xl:block">
              <p className="text-sm font-semibold text-white">{profile?.full_name ?? 'Executive user'}</p>
              <p className="text-xs text-slate-400">{profile?.title ?? 'Internal access'}</p>
            </div>
            <button
              type="button"
              className="rounded-full p-2 text-slate-400 hover:bg-white/10 hover:text-white"
              onClick={() => void signOut()}
              aria-label="Sign out"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </header>
  );
}
